/**
 * Copyright © 2026 Rémi Pace.
 * This file is part of Abc-Map.
 *
 * Abc-Map is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of
 * the License, or (at your option) any later version.
 *
 * Abc-Map is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General
 * Public License along with Abc-Map. If not, see <https://www.gnu.org/licenses/>.
 */
import type { Services } from './core/Services';
import { errorMessage, getAbcWindow, Logger, UserStatus } from '@abc-map/shared';
import type { AxiosError } from 'axios';
import { HttpError } from './core/http/HttpError';
import { render } from './render';
import type { MainStore } from './store/store';
import { ProjectEventType } from './core/project/ProjectEvent';
import { StyleFactory } from './core/geo/styles/StyleFactory';
import { ModuleRegistry } from './core/modules/registry/ModuleRegistry';
import { UiActions } from './store/ui/actions';
import { initMainDatabase } from './core/storage/indexed-db/main-database';
import { Routes } from './routes';
import { matchRoutes } from 'react-router-dom';

export const logger = Logger.get('bootstrap.tsx');

export function bootstrap(svc: Services, store: MainStore) {
  return authentication(svc)
    .then(() => initializeModules())
    .then(() => render(svc, store))
    .then(() => dispatchVisit(store))
    .then(() => initProject(svc, store))
    .then(() => enableGeolocation(svc, store))
    .catch((err) => bootstrapError(svc, err));
}

/**
 * All users are authenticated, as connected users or as anonymous users
 * @param svc
 */
async function authentication(svc: Services): Promise<void> {
  const { authentication, project, pwa } = svc;

  // On logout we clear all data
  authentication.addDisconnectListener(() => {
    project
      .newProject()
      .catch((err) => logger.error('New project error: ', err))
      .then(() => svc.storage.clearStorage())
      .catch((err) => logger.error('Clear storage error: ', err));
  });

  // If we ar offline, we do not try to log in
  if (!pwa.isOnline()) {
    return;
  }

  const connected = !!authentication.getUserStatus();
  if (connected) {
    return authentication.renewToken().catch((err) => {
      logger.warn('Cannot renew token, session may have expired: ', err);
      return authentication.anonymousLogin();
    });
  }
  // Else we authenticate as anonymous
  else {
    return authentication.anonymousLogin();
  }
}

function dispatchVisit(store: MainStore) {
  store.dispatch(UiActions.incrementVisitCounter());
}

async function initProject(services: Services, store: MainStore) {
  const { project: projectService, geo, history, authentication } = services;

  await initMainDatabase().catch((err) => logger.error('Unable to initialize main database:', err));

  // When project loaded, we clean style cache and undo/redo history
  projectService.addProjectLoadedListener((ev) => {
    if (ProjectEventType.ProjectLoaded === ev.type) {
      history.resetHistory();
      StyleFactory.get().clearCache();
    } else {
      logger.error('Unhandled event type: ', ev);
    }
  });

  // When main map move we save view in project
  geo.getMainMap().addViewMoveListener(() => {
    const view = geo.getMainMap().getView();
    projectService.setView(view);
  });

  try {
    // There are always a new project loaded, even at a fresh start.
    // If we are displaying a shared map we skip project loading, it will be loaded by SharedMapView.
    // If not a project may have been stored locally or remotely, we try to load it if any.
    const isSharedMapRoute = matchRoutes([{ path: Routes.sharedMap().raw() }], window.location);
    const userIsAuthenticated = authentication.getUserStatus() === UserStatus.Authenticated;

    if (!isSharedMapRoute) {
      const previous = store.getState().project.metadata;
      if (await projectService.isStoredLocally(previous.id)) {
        await projectService.loadLocalProject(previous.id);
      }
      // Otherwise we try to find it online
      else if (userIsAuthenticated) {
        await projectService.loadRemotePrivateProject(previous.id);
      }
      // Otherwise we initialize a new project
      else {
        await projectService.newProject();
      }
    }
  } catch (err) {
    logger.error(`Local project init error:`, err);
    await projectService.newProject();
  }

  // We must not save before project loading
  projectService.enableProjectAutoSave();
}

// Enable and disable geolocation according to store on the first map display
function enableGeolocation(svc: Services, store: MainStore) {
  const map = svc.geo.getMainMap();
  const geolocationEnabled = store.getState().map.geolocation.enabled;
  const followPosition = store.getState().map.geolocation.followPosition;
  const rotateMap = store.getState().map.geolocation.rotateMap;

  if (geolocationEnabled) {
    map.enableGeolocation();
    map.getGeolocation()?.followPosition(followPosition);
    map.getGeolocation()?.rotateMap(rotateMap);
    map.getGeolocation()?.onNextAccuracyChange(() => map.getGeolocation()?.updateMapView());
  } else {
    map.disableGeolocation();
  }
}

function bootstrapError(svc: Services, err: Error | AxiosError | undefined): Promise<void> {
  logger.error('Bootstrap error: ', err);
  const { storage } = svc;

  let message: string;
  if (HttpError.isTooManyRequests(err)) {
    message = '<div>You have exceeded the number of authorized requests 😭. Please try again later.</div>';
  } else {
    message = '<div>Small technical issue 😅 Please try again later.</div>';

    // If there's a problem with local data, we let users clear it.
    const window = getAbcWindow();
    window.abc = {
      ...window.abc,
      resetLocalData: () => {
        storage
          .resetStorage()
          .catch((err) => logger.error('Clear error: ', err))
          .finally(() => window.location.reload());
      },
    };
    message += '<div>If this error persist, you can try to ';
    message += '<button onclick="window.abc.resetLocalData()" class="btn btn-link p-0 mb-1">clean your local data.</button></div>';
  }

  const root = document.querySelector('#root');
  if (root) {
    root.innerHTML = `
    <h1 class='text-center my-5'>Abc-Map</h1>
    <div class='d-flex flex-column justify-content-center align-items-center my-5'>${message}</div>
  `;
  } else {
    alert(message);
  }

  return Promise.reject(err ?? new Error(errorMessage(err)));
}

async function initializeModules(): Promise<void> {
  await ModuleRegistry.get().initialize();
}
